import { ModuleType, WorkflowExpertLetterExhibit } from "@codegen/enums";
import {
  FormContainer,
  OnboardingContainer,
  OnboardingFormField,
} from "@components/onboarding";
import { useReadOnlyMode } from "@components/readOnlyModeProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { UnexpectedErrorPage } from "@pages/onboarding/shared/unexpected-error";
import { OnboardingPageType } from "@pages/onboarding/types";
import { useDebounceCallback } from "@react-hook/debounce";
import { StrategyModule } from "@utils/types";
import * as React from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { z } from "zod";

export const expertLetterFormSchema = z.object({
  fullName: z.string().min(2, "Required"),
  email: z.string().email("Required"),
  title: z.string().min(2, "Required"),
  currentCompanyName: z.string().min(1, "Required"),
  linkedin: z
    .string()
    .url("Please provide a valid LinkedIn URL")
    .min(1, "Required"),
  description: z
    .string()
    .min(100, "Please provide more context in 100+ characters.")
    .optional(),
  meetDescription: z
    .string()
    .min(20, "Please provide more context in 20+ characters"),
  phoneNumber: z
    .string()
    .refine((val) => val === "" || val.length >= 10, {
      message: "Please enter a 10 digit phone number",
    })
    .optional(),
  monthMet: z.string().min(2, "Required"),
  yearMet: z.string().min(4, "Required"),
});

export type ExpertLetterFormType = z.infer<typeof expertLetterFormSchema>;
export const ExpertLetterForm: React.FC<
  OnboardingPageType & {
    title: string;
    subtitle: React.ReactNode;
    description: React.ReactNode;
    descriptionLabel?: React.ReactNode;
    includeDescription?: boolean;
  }
> = ({
  module,
  exhibit,
  onBack,
  onSubmit,
  onSkip,
  isSaving,
  title,
  subtitle,
  description,
  includeDescription,
  descriptionLabel,
  footnote,
  autoSave,
  saveData,
  onSkipForNow,
  moduleProgress,
  currentStep,
  showSkipForNow,
  hideExitButton,
}) => {
  const { required, data } = exhibit;
  const { isReadOnly } = useReadOnlyMode();
  const [status, setStatus] = React.useState<
    "idle" | "loading" | "error" | "success"
  >("idle");

  // Track pending save operations to avoid race conditions
  const pendingSaveRef = React.useRef<Promise<void> | null>(null);

  const methods = useForm({
    defaultValues: {
      ...(data as ExpertLetterFormType),
    },
    resolver: zodResolver(expertLetterFormSchema),
  });

  const { control } = methods;

  const handleSubmit = async (data: ExpertLetterFormType) => {
    // Wait for any pending auto-save to complete
    if (pendingSaveRef.current) {
      await pendingSaveRef.current;
    }

    setStatus("loading");
    onSubmit(data);
  };

  // Watch form values
  const watchedValues = useWatch({
    control,
  });

  const saveForm = async (values: ExpertLetterFormType) => {
    const savePromise = (async () => {
      if (values) {
        try {
          setStatus("loading");
          await autoSave(values);
          saveData(module, exhibit, values);
          setStatus("success");
        } finally {
          pendingSaveRef.current = null;
        }
      }
    })();

    pendingSaveRef.current = savePromise;
    await savePromise;
  };

  const debouncedSave = useDebounceCallback(saveForm, 300);

  React.useEffect(() => {
    debouncedSave(watchedValues);
  }, [watchedValues, debouncedSave]);

  return (
    <FormProvider {...methods}>
      <OnboardingContainer
        title={title}
        subtitle={subtitle}
        description={description}
        onSubmit={methods.handleSubmit(handleSubmit)}
        exhibit={exhibit}
        name={`expert-letter-form`}
        footnote={footnote}
        onBack={onBack}
        onSkipForNow={required ? onSkipForNow : onSkip}
        isLoading={isSaving || status === "loading"}
        progress={moduleProgress}
        currentStep={currentStep}
        showSkipForNow={showSkipForNow}
        hideExitButton={hideExitButton}
      >
        <FormContainer status={status}>
          <div className="flex flex-col gap-3">
            <OnboardingFormField
              name="fullName"
              label="Signatory Full Name"
              placeholder="Signatory Full Name"
              type="text"
              required
              disabled={isReadOnly}
            />

            <div className="flex gap-3 justify-between">
              <OnboardingFormField
                name="title"
                label="Expert Current Title"
                placeholder="Chief Technology Officer"
                type="text"
                required
                disabled={isReadOnly}
                className="w-1/2"
              />

              <OnboardingFormField
                name="currentCompanyName"
                label="Expert Current Company Name"
                placeholder="Company Name"
                type="text"
                required
                disabled={isReadOnly}
                className="w-1/2"
              />
            </div>

            <div className="flex gap-3 justify-between">
              <OnboardingFormField
                name="email"
                label="Email"
                placeholder="john@doe.com"
                type="email"
                required
                disabled={isReadOnly}
                className="w-1/2"
              />

              <OnboardingFormField
                name="phoneNumber"
                label="Phone number (Optional)"
                placeholder="222-111-3333"
                type="text"
                disabled={isReadOnly}
                className="w-1/2"
              />
            </div>
          </div>

          <OnboardingFormField
            name="linkedin"
            label="Please share this individual's LinkedIn profile. We only need one individual to sign this letter."
            placeholder="https://www.linkedin.com/john-doe"
            type="text"
            required
            disabled={isReadOnly}
          />

          <OnboardingFormField
            name="meetDescription"
            label="Please share how and when you met this individual"
            placeholder="We met at..."
            type="text"
            required
            disabled={isReadOnly}
          />

          <div className="flex gap-3 items-center justify-between">
            <OnboardingFormField
              name="monthMet"
              label="Month Met"
              type="select"
              required
              className="w-1/2"
              disabled={isReadOnly}
              selectOptions={[
                { value: "", label: "Select Month" },
                { value: "january", label: "January" },
                { value: "february", label: "February" },
                { value: "march", label: "March" },
                { value: "april", label: "April" },
                { value: "may", label: "May" },
                { value: "june", label: "June" },
                { value: "july", label: "July" },
                { value: "august", label: "August" },
                { value: "september", label: "September" },
                { value: "october", label: "October" },
                { value: "november", label: "November" },
                { value: "december", label: "December" },
              ]}
            />

            <OnboardingFormField
              name="yearMet"
              label="Year Met"
              type="select"
              required
              className="w-1/2"
              disabled={isReadOnly}
              selectOptions={[
                { value: "", label: "Select Year" },
                ...Array.from({ length: 100 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return { value: year.toString(), label: year.toString() };
                }),
              ]}
            />
          </div>

          {includeDescription && (
            <OnboardingFormField
              name="description"
              label={descriptionLabel ?? "Description of role"}
              placeholder="Enter text here..."
              type="textarea"
              required
              disabled={isReadOnly}
            />
          )}
        </FormContainer>
      </OnboardingContainer>
    </FormProvider>
  );
};

export const ExpertLetterFooter: React.FC = () => {
  return (
    <p className="text-sm text-muted-foreground">
      We'll use this information to draft an expert letter. You will have the
      opportunity to review before we send the letter for signature. Please
      input the details from your perspective, our team will modify the language
      to your signatory's view.
    </p>
  );
};

export const ExpertLetterCriticalRoleForm: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name, company } = exhibit as WorkflowExpertLetterExhibit;
  const description = (
    <p className="text-sm text-muted-foreground mt-1">
      This should be someone from <b>{company?.companyName || "the company"}</b>{" "}
      who can attest to your critical role work and who will credit you with
      company wins.
    </p>
  );
  const label = (
    <div className="flex flex-col gap-2">
      <p className="font-semibold">
        Please provide 3-4 responsibilities for your role:
      </p>
      <ol className="list-decimal list-inside mb-3 flex flex-col gap-2">
        <li className="">
          Do you have control or authority over any budgets or project
          timelines?
        </li>
        <li className="">
          If you are technical, did you develop novel technology for the
          company?
        </li>
        <li className="leading-4">
          What was the impact of your work for the company? For example, did you
          help the company increase earnings by $X? Secure new clients or new
          investors?
        </li>
      </ol>
    </div>
  );
  return (
    <ExpertLetterForm
      {...props}
      subtitle={name}
      description={description}
      title={module.name}
      includeDescription
      descriptionLabel={label}
      footnote={<ExpertLetterFooter />}
    />
  );
};

export const ExpertLetterMembershipForm: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name } = exhibit as WorkflowExpertLetterExhibit;

  const description = (
    <p className="text-sm text-muted-foreground mt-1">
      The best signatory for this letter is an organizer, leader, or admissions
      director for the membership. Our team will use this information to draft a
      letter on their behalf and will send it to you for review.
    </p>
  );
  return (
    <ExpertLetterForm
      {...props}
      subtitle={name}
      description={description}
      title={module.name}
      footnote={<ExpertLetterFooter />}
    />
  );
};

export const ExpertLetterJudgingForm: React.FC<OnboardingPageType> = (
  props
) => {
  const { exhibit, module } = props;
  const { name } = exhibit as WorkflowExpertLetterExhibit;
  const description = (
    <p className="text-sm text-muted-foreground mt-1">
      The best signatory for this letter is an organizer, leader, or admissions
      director for the Judging event. Our team will use this information to
      draft a letter on their behalf and will send it to you for review.
    </p>
  );
  return (
    <ExpertLetterForm
      {...props}
      subtitle={name}
      description={description}
      title={module.name}
      footnote={
        <p className="text-sm text-muted-foreground">
          We'll use this information to draft an expert letter. You will have
          the opportunity to review before we send the letter for signature.
        </p>
      }
    />
  );
};

export const ExpertLetterAwardsForm: React.FC<OnboardingPageType> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit as WorkflowExpertLetterExhibit;
  const description = (
    <p>Ideally an individual from the awarding organization.</p>
  );
  return (
    <ExpertLetterForm
      {...props}
      subtitle={name}
      description={description}
      title={module.name}
      footnote={<ExpertLetterFooter />}
    />
  );
};

export const ExpertLetterOriginalContributionForm: React.FC<
  OnboardingPageType
> = (props) => {
  const { exhibit, module } = props;
  const { name } = exhibit as WorkflowExpertLetterExhibit;
  const description = (
    <p className="text-sm text-muted-foreground mt-1">
      This could be someone who knows about what you've created and who will
      credit you with its major impact in the field. This impact should extend
      beyond a single company (unless the company is a global leader, think
      Google, Meta, or OpenAI, etc.). The signatory maybe someone you work with
      or a fellow expert in the field.
    </p>
  );
  return (
    <ExpertLetterForm
      {...props}
      subtitle={name}
      description={description}
      title={module.name}
      includeDescription
      descriptionLabel={
        <div>
          <p className="font-semibold mb-2">
            Please complete the following sentence
          </p>
          <p className="leading-5">
            "I am the creator of an original X. My creation is called Z. I
            leveraged knowledge of _____________ to design Z. Z is considered
            totally original because _______. My work is considered remarkable
            in the field because_________."
          </p>
        </div>
      }
      footnote={<ExpertLetterFooter />}
    />
  );
};

export const ExpertLetterAOLetterForm: React.FC<OnboardingPageType> = (
  props
) => {
  const { petition, module } = props;
  const { companyByPetitionerId } = petition;
  const description = (
    <div className="flex flex-col gap-2 text-primary font-semibold text-xl leading-7">
      <p>Please provide us with the name of an individual who</p>
      <ol className="flex flex-col gap-1 list-decimal pl-4 text-sm text-muted-foreground mt-1">
        <li>Is based in the U.S. (not required to be a U.S. citizen)</li>
        <li>Who has 5-10 years of experience in your field</li>
        <li>Who is familiar with your work</li>
        <li>
          Who has no financial ties to {companyByPetitionerId?.dbaName} (Not a
          current investor, customer, or team member).
        </li>
        <li>
          And who will happily sign a letter that says "I support{" "}
          {petition.userByBeneficiaryId?.fullName}'s petition for a U.S. work
          visa."
        </li>
      </ol>
    </div>
  );
  return (
    <ExpertLetterForm
      {...props}
      subtitle={`Advisory Opinion Letter`}
      description={description}
      title={module.name}
      footnote={
        <p className="text-sm text-muted-foreground">
          We'll use this information to draft an expert letter. You will have
          the opportunity to review before we send the letter for signature.
        </p>
      }
    />
  );
};

const letterMap: Partial<{
  [key in StrategyModule["type"]]: typeof ExpertLetterCriticalRoleForm;
}> = {
  CRITICAL_ROLE: ExpertLetterCriticalRoleForm,
  ORIGINAL_CONTRIBUTION: ExpertLetterOriginalContributionForm,
  JUDGING: ExpertLetterJudgingForm,
  MEMBERSHIP: ExpertLetterMembershipForm,
  AWARDS: ExpertLetterAwardsForm,
  MEMO_STARTER: ExpertLetterAOLetterForm,
};

export const ExpertLetterRenderer: React.FC<OnboardingPageType> = (props) => {
  const { exhibit } = props;
  const { moduleType } = exhibit as WorkflowExpertLetterExhibit;
  const ExpertLetter = letterMap[moduleType as ModuleType];

  if (!ExpertLetter) {
    return (
      <UnexpectedErrorPage
        {...props}
        error={`Expert letter type ${moduleType} not found`}
      />
    );
  }

  return <ExpertLetter {...props} />;
};
